import { classes, icons, Loader } from '@portal/components'
import React, { useCallback, useMemo } from 'react'

import { buildQueryForEntireCollection, findCollectionName } from '../../utils'
import { useDQLEditorContext } from '../dqlEditor/context'

const { Empty } = icons

export default function CollectionsList() {
  const { appCollections, isLoadingCollections, query, setQuery, activeView } =
    useDQLEditorContext()

  const activeCollectionName = useMemo(
    () => findCollectionName(query || ''),
    [query],
  )

  const onQueryEntireCollection = useCallback(
    (collection: string, limit?: number) => {
      setQuery(
        buildQueryForEntireCollection(collection, limit),
        activeView === 'collections', // Only execute the query if we are in the collections view
      )
    },
    [setQuery, activeView],
  )

  return (
    <>
      {isLoadingCollections && (
        <div
          className="flex flex-1 flex-col items-center py-4"
          data-testid="collectionsLoading"
        >
          <Loader />
        </div>
      )}

      {!isLoadingCollections && !appCollections?.length && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-4 text-center">
          <Empty className="h-10 w-10" />
          <p className="font-medium text-foreground-normal">
            No collections found
          </p>
        </div>
      )}

      {appCollections?.length && appCollections?.length > 0 && (
        <div role="list" className="flex-1 overflow-auto px-2 py-1">
          {appCollections?.map((collection) => (
            <div
              role="listitem"
              key={collection}
              className={classes(
                'flex w-full items-center justify-between rounded-lg py-1 pl-3 pr-1 text-base hover:bg-background-surface',
                activeCollectionName === collection
                  ? 'bg-background-surface'
                  : '',
              )}
            >
              <span
                className="line-clamp-1 w-full cursor-pointer px-1 py-2 outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-outline"
                role="button"
                tabIndex={0}
                aria-label={`Query entire ${collection} collection`}
                onClick={() => onQueryEntireCollection(collection, 100)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onQueryEntireCollection(collection, 100)
                  }
                }}
              >
                {collection}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
